/**
 * Delivery Platforms Settings - Lieferando, Wolt, Uber Eats
 * Configure and monitor delivery platform integrations
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Truck, CheckCircle, Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function DeliveryPlatformSettings() {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState({
    lieferando: { enabled: false, restaurant_id: '' },
    wolt: { enabled: false, venue_id: '' },
    ubereats: { enabled: false, store_id: '' }
  });

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/delivery-platforms/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Load status error:', error);
    }
  };

  const copyWebhookUrl = (platform) => {
    const url = status?.[platform]?.webhook_url || '';
    navigator.clipboard.writeText(url);
    toast.success('Webhook-URL kopiert!', { description: 'In Portal-Einstellungen einfügen' });
  };

  const saveConfig = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/delivery-platforms/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Lieferportal-Einstellungen gespeichert!');
        loadStatus();
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const platforms = [
    {
      id: 'lieferando',
      name: 'Lieferando',
      logo: '🍕',
      color: 'orange',
      description: 'Marktführer in Deutschland'
    },
    {
      id: 'wolt',
      name: 'Wolt',
      logo: '⚡',
      color: 'blue',
      description: 'Schnell wachsend'
    },
    {
      id: 'ubereats',
      name: 'Uber Eats',
      logo: '🚗',
      color: 'green',
      description: 'International'
    }
  ];

  return (
    <Card className="bg-[#111C32] border-slate-700/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          <Truck className="w-5 h-5 text-teal-400" />
          Lieferportale
        </CardTitle>
        <p className="text-sm text-slate-400 mt-2">
          Verbinde dein Kassensystem mit Lieferando, Wolt & Uber Eats
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {platforms.map((platform) => {
          const platformStatus = status?.[platform.id];
          const platformConfig = config[platform.id];
          
          return (
            <div key={platform.id} className="p-6 bg-slate-800/30 rounded-xl border border-slate-700/50">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`text-4xl`}>{platform.logo}</div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">{platform.name}</h3>
                    <p className="text-sm text-slate-400">{platform.description}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {platformStatus && platformStatus.orders_today > 0 && (
                    <Badge variant="outline" className="text-emerald-400 border-emerald-400/50">
                      {platformStatus.orders_today} heute
                    </Badge>
                  )}
                  <Switch
                    checked={platformConfig.enabled}
                    onCheckedChange={(checked) => {
                      setConfig({
                        ...config,
                        [platform.id]: { ...platformConfig, enabled: checked }
                      });
                    }}
                    className="data-[state=checked]:bg-teal-500"
                  />
                </div>
              </div>

              {platformConfig.enabled && (
                <div className="space-y-4 mt-4 pt-4 border-t border-slate-700/50">
                  {/* Webhook URL */}
                  <div>
                    <Label className="text-slate-300 text-sm mb-2 block">Webhook-URL (in Portal eintragen)</Label>
                    <div className="flex gap-2">
                      <Input
                        value={platformStatus?.webhook_url || 'Laden...'}
                        readOnly
                        className="bg-[#0E1628] border-slate-700 text-slate-400 font-mono text-xs"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyWebhookUrl(platform.id)}
                        className="border-slate-600"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Platform-specific config */}
                  {platform.id === 'lieferando' && (
                    <div>
                      <Label className="text-slate-300 text-sm">Restaurant-ID</Label>
                      <Input
                        value={platformConfig.restaurant_id || ''}
                        onChange={(e) => setConfig({
                          ...config,
                          lieferando: { ...platformConfig, restaurant_id: e.target.value }
                        })}
                        placeholder="123456"
                        className="mt-1 bg-[#0E1628] border-slate-700 text-white"
                      />
                    </div>
                  )}

                  {/* Setup Guide Link */}
                  <a
                    href={`https://docs.${platform.id}.com/webhooks`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Setup-Anleitung öffnen
                  </a>
                </div>
              )}
            </div>
          );
        })}

        <Button
          onClick={saveConfig}
          className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-white h-12"
        >
          Einstellungen speichern
        </Button>

        {/* Info Box */}
        <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
          <div className="flex gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <div className="text-sm text-slate-300">
              <strong className="text-emerald-400">Status:</strong> Lieferportal-Integration ist aktiv! 
              Bestellungen von Lieferando, Wolt & Uber Eats werden automatisch im System angezeigt.
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
